import { CssBaseline } from "@mui/material";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createEditor, Editor, Node, Range, Transforms } from "slate";
import { Slate, Editable, withReact } from "slate-react";
import { Element, Leaf } from "./components/RenderLeafElement";
import { NoteTabToolbar } from "./components/SampleToolbars";
import { StyledDiv } from "./styled";
import { TextEditorProps } from "./types";
import isUrl from "is-url";
import { ReactEditor } from "slate-react";
import {
    wrapLink,
    insertImage,
    isImageUrl,
    toggleMark,
} from "./components/utils";
import isHotkey, { isKeyHotkey } from "is-hotkey";
import { HOTKEYS } from "../constants/common";
import { withHistory } from "slate-history";

const TextEditor = ({
    body,
    setBody = null,
    readOnly = false,
    readOnlyStyle = "",
    align = "center",
    Toolbar = <NoteTabToolbar align="flex-start" />,
    editorFocus = 0,
}: TextEditorProps) => {
    /**
     * Wraps the text editor with functionality to perform hyperlink on a inline text
     * @param editor a slate text editor object
     */
    const TextEditorWrapper = (editor: any) => {
        const { insertData, insertText, isInline, isVoid } = editor;

        editor.isVoid = (element: any) => {
            return element.type === "image" ? true : isVoid(element);
        };

        editor.isInline = (element: any) =>
            ["link", "button"].includes(element.type) || isInline(element);

        editor.insertText = (text: any) => {
            if (text && isUrl(text)) {
                wrapLink(editor, text);
            } else {
                insertText(text);
            }
        };

        editor.insertData = (data: any) => {
            const text = data.getData("text/plain");
            const { files } = data;

            if (files && files.length > 0) {
                for (const file of files) {
                    const reader = new FileReader();
                    const [mime] = file.type.split("/");

                    if (mime === "image") {
                        reader.addEventListener("load", () => {
                            const url = reader.result;
                            insertImage(editor, url);
                        });

                        reader.readAsDataURL(file);
                    }
                }
            } else if (isImageUrl(text)) {
                insertImage(editor, text);
            } else {
                insertData(data);
            }
        };

        return editor;
    };

    /**
     * onKeyDown is an event handler that processes keydown events (e.g. hotkeys)
     * @param event An event object
     */
    const onKeyDown = (event: any) => {
        for (const hotkey in HOTKEYS) {
            if (isHotkey(hotkey, event as any)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                toggleMark(editor, mark);
            }
        }

        if (event.key === "Tab") {
            event.preventDefault();
            editor.insertText("\t");
        }

        const { selection } = editor;
        // Default left/right behavior is unit:'character'.
        // This fails to distinguish between two cursor positions, such as
        // <inline>foo<cursor/></inline> vs <inline>foo</inline><cursor/>.
        // Here we modify the behavior to unit:'offset'.
        // This lets the user step into and out of the inline without stepping over characters.
        // You may wish to customize this further to only use unit:'offset' in specific cases.
        if (selection && Range.isCollapsed(selection)) {
            const { nativeEvent } = event;
            if (isKeyHotkey("left", nativeEvent)) {
                event.preventDefault();
                Transforms.move(editor, { unit: "offset", reverse: true });
                return;
            }
            if (isKeyHotkey("right", nativeEvent)) {
                event.preventDefault();
                Transforms.move(editor, { unit: "offset" });
                return;
            }
        }
    };

    // editor is a slate text editor object
    const editor = useMemo(
        () => TextEditorWrapper(withReact(withHistory(createEditor()))),
        []
    );

    // TODO - need to make this not so custom
    useEffect(() => {
        if (editorFocus > 0) {
            ReactEditor.focus(editor);
        }
    }, [editorFocus]);

    /**
     * Renders block formatting using Element function
     * @returns {jsx}
     */
    const renderElement = useCallback((props) => {
        return <Element {...props} />;
    }, []);

    /**
     * Renders font formatting using Leaf function
     * @returns {jsx}
     */
    const renderLeaf = useCallback((props) => {
        return <Leaf {...props} />;
    }, []);

    return (
        <StyledDiv
            style={{ float: align }}
            className={`${readOnly ? "" : "editableRootShadow"}`}
        >
            <Slate
                editor={editor}
                value={body}
                onChange={(newValue: React.SetStateAction<Node[]>) => {
                    if (!readOnly) setBody(newValue);
                }}
            >
                <CssBaseline />
                {readOnly ? null : Toolbar}
                <Editable
                    className={clsx(
                        readOnly ? readOnlyStyle : "editableTextbox"
                    )}
                    readOnly={readOnly}
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    onKeyDown={onKeyDown}
                />
            </Slate>
        </StyledDiv>
    );
};
export default TextEditor;
